(function () {
  // Shared carousel. data-init-manual delegates activation to a visibility owner.
  if (customElements.get("theme-carousel")) {
    return;
  }

class ThemeCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.isInitialized = false;
    this.prevButton = null;
    this.nextButton = null;
    this.readyFrame = null;
    this.initObserver = null;
    this.libraryListeners = null;
    this.pendingActivateOptions = null;
  }

  connectedCallback() {
    if (this.hasAttribute("data-init-manual")) return;
    if (this.dataset.static === "true") {
      this.setReadyState(true);
      return;
    }

    if (!this.isVisible()) {
      return;
    }

    if (this.shouldInitImmediately()) {
      this.init();
      return;
    }

    this.observeForInit();
  }

  disconnectedCallback() {
    this.destroy();
  }

  setReadyState(isReady) {
    this.dataset.layoutReady = isReady ? "true" : "false";
    this.classList.toggle("is-ready", isReady);
  }

  queueReadyState() {
    if (this.readyFrame) {
      cancelAnimationFrame(this.readyFrame);
    }

    this.readyFrame = requestAnimationFrame(() => {
      this.readyFrame = requestAnimationFrame(() => {
        if (!this.swiper) {
          return;
        }

        this.swiper.update();
        this.updateEdgeState();
        this.setReadyState(true);
      });
    });
  }

  isVisible() {
    return !this.hidden && this.offsetParent !== null;
  }

  shouldInitImmediately() {
    if (this.dataset.initImmediate === "true") {
      return true;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const rect = this.getBoundingClientRect();
    return rect.top < viewportHeight + 200 && rect.bottom > -200;
  }

  observeForInit() {
    if (this.isInitialized || this.initObserver) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      this.init();
      return;
    }

    this.initObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        this.disconnectInitObserver();
        this.init();
      },
      { rootMargin: "600px 0px" }
    );
    this.initObserver.observe(this);
  }

  disconnectInitObserver() {
    if (!this.initObserver) {
      return;
    }

    this.initObserver.disconnect();
    this.initObserver = null;
  }

  getBooleanData(name) {
    return this.dataset[name] === "true";
  }

  usesNativeMobileScroll() {
    return this.getBooleanData("nativeMobile") && window.matchMedia("(max-width: 767.98px)").matches;
  }

  getNumberData(name, fallback) {
    const value = Number(this.dataset[name]);
    return Number.isNaN(value) ? fallback : value;
  }

  buildFreeMode(enabled) {
    if (!enabled) {
      return false;
    }

    return {
      enabled: true,
      momentum: true,
      momentumBounce: false,
    };
  }

  prepareActiveMedia() {
    if (!this.swiper) return;
    const activeSlide = this.swiper.slides?.[this.swiper.activeIndex];
    if (!activeSlide) return;

    activeSlide.querySelectorAll("video").forEach((video) => {
      if (video.dataset.poster) {
        video.poster = video.dataset.poster;
        delete video.dataset.poster;
      }

      let sourceAdded = false;
      video.querySelectorAll("source[data-src]").forEach((source) => {
        source.src = source.dataset.src;
        delete source.dataset.src;
        sourceAdded = true;
      });
      if (!sourceAdded) return;

      video.load();
      if (video.autoplay) {
        video.play().catch(() => {});
      }
    });
  }

  getConfig() {
    const mobile = this.dataset.mobile || "1";
    const tablet = this.dataset.tablet || "";
    const desktop = this.dataset.desktop || "4";
    const spacing = this.getNumberData("spacing", 20);
    const spacingMobile = this.getNumberData("spacingMobile", spacing);
    const spacingTablet = this.getNumberData("spacingTablet", spacing);
    const spacingDesktop = this.getNumberData("spacingDesktop", spacing);
    const speed = this.getNumberData("speed", 500);
    const autoplaySpeed = this.getNumberData("autoplaySpeed", 3) * 1000;
    const effect = this.dataset.effect || "slide";
    const loop = this.getBooleanData("loop");
    const autoplayStopsOnInteraction = this.getBooleanData("autoplayStopsOnInteraction");
    const autoHeight = this.getBooleanData("autoheight");
    const autoplayEnabled = this.getBooleanData("autoplay");
    const progressbar = this.getBooleanData("paginationProgressbar");
    const freeScrollMobile = this.getBooleanData("freeScrollMobile");
    const freeScrollTablet = this.getBooleanData("freeScrollTablet");
    const freeScrollDesktop = this.getBooleanData("freeScrollDesktop");
    const nativeMobile = this.getBooleanData("nativeMobile");
    const slidesPerGroup = this.getNumberData("slidespergroup", 1);

    let tabletSlides = tablet;
    if (!tabletSlides) {
      const desktopNumber = Number(desktop);

      if (desktopNumber < 2) {
        tabletSlides = 1;
      } else if (desktopNumber < 3) {
        tabletSlides = 2;
      } else {
        tabletSlides = 3;
      }
    }

    return {
      enabled: !nativeMobile,
      slidesPerView: mobile,
      spaceBetween: spacingMobile >= 15 ? 15 : spacingMobile,
      slidesPerGroup,
      speed,
      effect,
      loop,
      autoHeight,
      // Opt in for native video galleries, matching the product media gallery.
      // Focused videos still swipe, while native controls retain pointer starts.
      ...(this.getBooleanData("swipeVideos") ? {
        focusableElements: "input, select, option, textarea, button, label",
        touchStartPreventDefault: false,
      } : {}),
      watchOverflow: true,
      watchSlidesProgress: true,
      // Explicit owners can batch slide updates; other carousels keep automatic observation.
      observeParents: this.dataset.observeMutations !== "false",
      observer: this.dataset.observeMutations !== "false",
      resizeObserver: true,
      autoplay: autoplayEnabled
        ? {
            delay: autoplaySpeed,
            disableOnInteraction: autoplayStopsOnInteraction,
          }
        : false,
      freeMode: this.buildFreeMode(freeScrollMobile),
      navigation: {
        nextEl: this.querySelector(".swiper-button-next"),
        prevEl: this.querySelector(".swiper-button-prev"),
        disabledClass: "swiper-button-disabled",
      },
      pagination: {
        clickable: true,
        el: this.querySelector(".swiper-pagination"),
        type: progressbar ? "progressbar" : "bullets",
      },
      on: {
        init: () => {
          this.updateEdgeState();
          this.prepareActiveMedia();
        },
        afterInit: () => {
          this.updateEdgeState();
          this.prepareActiveMedia();
        },
        slideChange: () => {
          this.updateEdgeState();
          this.prepareActiveMedia();
        },
        update: () => this.updateEdgeState(),
        resize: () => this.updateEdgeState(),
        reachBeginning: () => this.updateEdgeState(),
        reachEnd: () => this.updateEdgeState(),
        fromEdge: () => this.updateEdgeState(),
      },
      breakpoints: {
        768: {
          enabled: true,
          slidesPerView: tabletSlides,
          spaceBetween: spacingTablet >= 30 ? 30 : spacingTablet,
          freeMode: this.buildFreeMode(freeScrollTablet),
        },
        1025: {
          enabled: true,
          slidesPerView: desktop,
          spaceBetween: spacingDesktop,
          freeMode: this.buildFreeMode(freeScrollDesktop),
        },
      },
    };
  }

  updateEdgeState() {
    if (!this.swiper) {
      return;
    }

    if (!this.prevButton) {
      this.prevButton = this.querySelector(".swiper-button-prev");
    }

    if (!this.nextButton) {
      this.nextButton = this.querySelector(".swiper-button-next");
    }

    const loops = this.swiper.params.loop;
    const previousDisabled = this.swiper.isLocked || (!loops && this.swiper.isBeginning);
    const nextDisabled = this.swiper.isLocked || (!loops && this.swiper.isEnd);
    if (this.prevButton) {
      this.prevButton.classList.toggle("is-disabled", previousDisabled);
      this.prevButton.setAttribute(
        "aria-hidden",
        previousDisabled ? "true" : "false"
      );
    }

    if (this.nextButton) {
      this.nextButton.classList.toggle("is-disabled", nextDisabled);
      this.nextButton.setAttribute(
        "aria-hidden",
        nextDisabled ? "true" : "false"
      );
    }
  }

  init() {
    if (this.isInitialized || !this.isVisible()) {
      return;
    }

    // Module/custom-element callbacks can run before the layout's deferred library.
    if (typeof window.Swiper !== "function") {
      if (!this.libraryListeners) {
        this.libraryListeners = new AbortController();
        const options = { once: true, signal: this.libraryListeners.signal };
        const ready = () => {
          if (typeof window.Swiper !== "function") return;
          this.libraryListeners?.abort();
          this.libraryListeners = null;
          if (this.isConnected) this.init();
        };
        const script = document.querySelector('script[src*="/swiper-bundle.min.js"]');
        script?.addEventListener("load", ready, options);
        script?.addEventListener("error", () => {
          this.libraryListeners?.abort();
          this.libraryListeners = null;
        }, options);
        document.addEventListener("DOMContentLoaded", ready, options);
      }
      return;
    }

    this.libraryListeners?.abort();
    this.libraryListeners = null;
    this.disconnectInitObserver();
    this.setReadyState(false);
    this.prevButton = this.querySelector(".swiper-button-prev");
    this.nextButton = this.querySelector(".swiper-button-next");
    this.swiper = new Swiper(this, this.getConfig());
    this.isInitialized = true;
    this.queueReadyState();

    if (this.pendingActivateOptions?.reset === true) {
      this.swiper.slideTo(0, 0);
    }
    this.pendingActivateOptions = null;
  }

  activate(options = {}) {
    if (!this.isInitialized) {
      if (!this.shouldInitImmediately()) {
        this.pendingActivateOptions = { ...this.pendingActivateOptions, ...options };
        this.observeForInit();
        return;
      }

      this.init();
    }

    if (!this.swiper) {
      return;
    }

    if (this.usesNativeMobileScroll()) {
      if (options.reset === true) {
        this.scrollTo({ left: 0, behavior: "auto" });
      }
      this.setReadyState(true);
      return;
    }

    this.swiper.update();
    this.updateEdgeState();
    this.queueReadyState();

    if (options.reset === true) {
      this.swiper.slideTo(0, 0);
    }
  }

  destroy() {
    this.libraryListeners?.abort();
    this.libraryListeners = null;
    this.disconnectInitObserver();
    this.swiper?.destroy(true, true);
    this.swiper = null;
    this.isInitialized = false;
    this.prevButton = null;
    this.nextButton = null;
    this.pendingActivateOptions = null;
    if (this.readyFrame) {
      cancelAnimationFrame(this.readyFrame);
      this.readyFrame = null;
    }
    this.setReadyState(false);
  }
}

customElements.define("theme-carousel", ThemeCarousel);
})();
